import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import stlModelsData from '../data/stl_models.json';
import * as amplitude from '@amplitude/analytics-browser';

// STL Model Card Component
const StlModelCard = ({ model, theme }) => {
  const [imageError, setImageError] = useState(false);
  
  // Define colors based on theme
  const cardBgColor = theme === 'light' ? 'white' : '#2d2d2d';
  const textColor = theme === 'light' ? '#333' : 'white';
  const secondaryTextColor = theme === 'light' ? '#666' : '#aaa';
  
  // Generate the image path based on the model filename
  const getImagePath = () => {
    // Use the same name as the STL file but with .png extension
    const baseName = model.filename.replace(/\.stl$/i, '.png');
    return `/stl_files/${baseName}`;
  };
  
  // Handle image loading error
  const handleImageError = () => {
    console.error(`Failed to load thumbnail for ${model.filename}`);
    setImageError(true);
    
    // Track image loading error
    amplitude.track('Model Thumbnail Error', {
      modelId: model.id,
      modelName: model.name,
      filename: model.filename
    });
  };
  
  // Determine model type for fallback image
  const getModelType = () => {
    const filename = model.filename.toLowerCase();
    const tags = model.tags ? model.tags.map(tag => tag.toLowerCase()) : [];
    
    if (filename.includes('keychain') || tags.some(tag => tag.includes('keychain'))) {
      return 'keychain';
    } else if (filename.includes('fidget') || tags.some(tag => tag.includes('fidget'))) {
      return 'fidget';
    } else if (filename.includes('dino') || filename.includes('t-rex') || tags.some(tag => tag.includes('dinosaur'))) {
      return 'dinosaur';
    } else if (filename.includes('spiral') || tags.some(tag => tag.includes('spiral'))) {
      return 'spiral';
    } else {
      return 'generic';
    }
  };
  
  // Get fallback image based on model type
  const getFallbackImage = () => {
    const modelType = getModelType();
    return `/images/model_types/${modelType}.svg`;
  };
  
  return (
    <div className="card" style={{ backgroundColor: cardBgColor }}>
      <div className="card-image" style={{ height: '200px', position: 'relative' }}>
        {!imageError ? (
          <img 
            src={getImagePath()} 
            alt={model.name}
            onError={handleImageError}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              backgroundColor: theme === 'light' ? '#f8f8f8' : '#333333'
            }}
          />
        ) : (
          <div 
            style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: theme === 'light' ? '#f8f8f8' : '#333333',
              color: theme === 'light' ? '#888' : '#aaa'
            }}
          >
            <img 
              src={getFallbackImage()} 
              alt={`${model.name} (generic preview)`}
              style={{ 
                maxWidth: '80%', 
                maxHeight: '80%', 
                opacity: theme === 'light' ? 0.7 : 0.6
              }}
            />
          </div>
        )}
        <span 
          className="card-title" 
          style={{ 
            position: 'absolute', 
            bottom: 0, 
            left: 0, 
            width: '100%', 
            padding: '10px', 
            backgroundColor: 'rgba(0,0,0,0.5)',
            margin: 0,
            fontSize: '1.2rem'
          }}
        >
          {model.name}
        </span>
      </div>
      <div className="card-content" style={{ color: textColor }}>
        <p style={{ height: '60px', overflow: 'hidden', marginBottom: '10px' }}>{model.description}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', color: secondaryTextColor, fontSize: '0.9rem' }}>
          <span>By {model.author}</span>
          <span>{model.date}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
          <span style={{ color: secondaryTextColor }}>
            <i className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>file_download</i> {model.downloads}
          </span>
          <span style={{ color: secondaryTextColor }}>
            <i className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>favorite</i> {model.likes}
          </span>
        </div>
      </div>
      <div className="card-action">
        <a 
          href={`/stl_files/${model.filename}`} 
          download 
          className="btn waves-effect waves-light blue"
          onClick={() => amplitude.track('Model Downloaded', {
            modelId: model.id,
            modelName: model.name,
            filename: model.filename,
            author: model.author
          })}
        >
          <i className="material-icons left">file_download</i>
          Download
        </a>
        <Link 
          to={`/stl-models/${model.id}`} 
          className="btn-flat waves-effect"
          style={{ color: theme === 'light' ? '#1e88e5' : '#64b5f6' }}
          onClick={() => amplitude.track('Model View Clicked', {
            modelId: model.id,
            modelName: model.name,
            filename: model.filename,
            author: model.author
          })}
        >
          <i className="material-icons left">visibility</i>
          View
        </Link>
        <a 
          href="#!" 
          className="btn-flat waves-effect green-text"
          onClick={() => amplitude.track('Model Order Clicked', {
            modelId: model.id,
            modelName: model.name,
            filename: model.filename,
            author: model.author
          })}
        >
          <i className="material-icons left">shopping_cart</i>
          Order
        </a>
      </div>
    </div>
  );
};

// Main STL Models Page Component
const StlModels = ({ theme }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Load models from JSON file
  useEffect(() => {
    // In a real application, this would be an API call
    // For now, we're using the local JSON file
    setModels(stlModelsData.models);
    setLoading(false);
    
    // Track page view
    amplitude.track('Page Viewed', {
      page: 'STL Models Gallery',
      theme: theme,
      modelCount: stlModelsData.models.length
    });
  }, [theme]);
  
  // Define text color based on theme
  const themeTextColor = theme === 'light' ? '#333' : 'white';
  const inputStyle = theme === 'dark' ? {
    color: themeTextColor,
    backgroundColor: '#444',
    border: '1px solid #555',
    borderRadius: '4px',
    padding: '0 10px'
  } : {};
  
  // Filter models based on search term
  const filteredModels = models.filter(model => 
    model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (model.tags && model.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );
  
  // Sort models based on selected option
  const sortedModels = [...filteredModels].sort((a, b) => {
    switch (sortOption) {
      case 'newest':
        return new Date(b.date) - new Date(a.date);
      case 'oldest':
        return new Date(a.date) - new Date(b.date);
      case 'downloads':
        return b.downloads - a.downloads;
      case 'likes':
        return b.likes - a.likes;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });
  
  return (
    <div className="stl-models-container container">
      <h1 className="center-align" style={{ color: themeTextColor }}>3D Model Gallery</h1>
      
      <div className="row" style={{ marginBottom: '30px' }}>
        <div className="col s12 m6">
          <div className="input-field">
            <i className="material-icons prefix" style={{ color: themeTextColor }}>search</i>
            <input
              id="search"
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                
                // Only track search if there's actual input (not on empty or backspace to empty)
                if (e.target.value.trim()) {
                  // Debounce search tracking to avoid excessive events
                  if (window.searchTrackTimeout) {
                    clearTimeout(window.searchTrackTimeout);
                  }
                  
                  window.searchTrackTimeout = setTimeout(() => {
                    amplitude.track('Model Search', {
                      searchTerm: e.target.value,
                      resultCount: filteredModels.filter(model => 
                        model.name.toLowerCase().includes(e.target.value.toLowerCase()) ||
                        model.description.toLowerCase().includes(e.target.value.toLowerCase()) ||
                        (model.tags && model.tags.some(tag => tag.toLowerCase().includes(e.target.value.toLowerCase())))
                      ).length
                    });
                  }, 500);
                }
              }}
              style={inputStyle}
              className={theme === 'dark' ? 'white-text' : ''}
            />
            <label htmlFor="search" className={searchTerm ? "active" : ""}>Search Models</label>
          </div>
        </div>
        <div className="col s12 m6">
          <div className="input-field">
            <i className="material-icons prefix" style={{ color: themeTextColor }}>sort</i>
            <select
              id="sort-select"
              className={`browser-default ${theme === 'dark' ? 'grey darken-3 white-text' : ''}`}
              value={sortOption}
              onChange={(e) => {
                setSortOption(e.target.value);
                
                // Track sort option change
                amplitude.track('Model Sort Changed', {
                  sortOption: e.target.value,
                  modelCount: filteredModels.length
                });
              }}
              style={{ 
                marginLeft: '45px',
                padding: '8px',
                borderRadius: '4px',
                border: theme === 'dark' ? '1px solid #555' : '1px solid #ccc'
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="downloads">Most Downloads</option>
              <option value="likes">Most Likes</option>
              <option value="name">Name (A-Z)</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="row">
        {loading ? (
          <div className="col s12 center-align">
            <h5 style={{ color: themeTextColor }}>Loading...</h5>
          </div>
        ) : sortedModels.length > 0 ? (
          sortedModels.map(model => (
            <div key={model.id} className="col s12 m6 l4" style={{ marginBottom: '20px' }}>
              <StlModelCard model={model} theme={theme} />
            </div>
          ))
        ) : (
          <div className="col s12 center-align">
            <h5 style={{ color: themeTextColor }}>No models found matching your search.</h5>
          </div>
        )}
      </div>
      
      <div className="fixed-action-btn">
        <a 
          href="/stl-generator" 
          className="btn-floating btn-large orange"
          onClick={() => amplitude.track('Create New STL Clicked', {
            fromPage: 'STL Models Gallery'
          })}
        >
          <i className="large material-icons">add</i>
        </a>
      </div>
    </div>
  );
};

export default StlModels;
